import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useTenantBranding } from '../../contexts/ThemeContext';
import { supabase } from '../../lib/supabaseClient';
import { Button } from '../ui/Button';

interface AcceptInviteFormValues {
  password: string;
  confirmPassword: string;
}

type VerifyState = 'pending' | 'ready' | 'expired';

export function AcceptInvitePage() {
  const navigate = useNavigate();
  const { branding } = useTenantBranding();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [verifyState, setVerifyState] = useState<VerifyState>('pending');
  // Lien d'invitation au portail client : `for=client` (posé par invite-client).
  const [isClient] = useState(
    () => new URL(window.location.href).searchParams.get('for') === 'client',
  );

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<AcceptInviteFormValues>();

  const passwordValue = watch('password');

  useEffect(() => {
    let cancelled = false;

    async function bootstrapSession() {
      const url = new URL(window.location.href);
      const tokenHash = url.searchParams.get('token_hash');
      const type = url.searchParams.get('type');

      if (tokenHash && (type === 'invite' || type === 'recovery')) {
        const { error } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type,
        });
        if (cancelled) return;
        if (error) {
          setVerifyState('expired');
          return;
        }
        url.searchParams.delete('token_hash');
        url.searchParams.delete('type');
        window.history.replaceState({}, '', url.pathname + url.search);
        setVerifyState('ready');
        return;
      }

      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      setVerifyState(data.session ? 'ready' : 'expired');
    }

    bootstrapSession();
    return () => {
      cancelled = true;
    };
  }, []);

  async function onSubmit(values: AcceptInviteFormValues) {
    setSubmitError(null);
    const { error } = await supabase.auth.updateUser({
      password: values.password,
    });

    if (error) {
      setSubmitError(
        error.message === 'Auth session missing!'
          ? 'Lien expiré ou invalide. Demandez une nouvelle invitation.'
          : 'Une erreur est survenue. Veuillez réessayer.',
      );
      return;
    }

    navigate(isClient ? '/portail' : '/dashboard', { replace: true });
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-cime-bg p-4">
      <div className="w-full max-w-[420px]">
        {/* Logo / Firm name */}
        <div className="text-center mb-8">
          <span
            className="text-xl font-bold text-cime-primary"
            style={{ fontFamily: 'var(--cime-font-heading)' }}
          >
            {branding.firm_name}
          </span>
        </div>

        <div className="bg-cime-surface rounded-cime border border-neutral-200 shadow-sm p-8">
          <h1
            className="text-2xl font-bold text-cime-primary mb-1"
            style={{ fontFamily: 'var(--cime-font-heading)' }}
          >
            {isClient ? 'Bienvenue dans votre espace client' : <>Bienvenue dans l&apos;équipe</>}
          </h1>
          <p className="text-sm text-neutral-500 mb-6">
            Définissez votre mot de passe pour accéder à votre espace.
          </p>

          {verifyState === 'pending' ? (
            <p className="text-sm text-neutral-500 py-8 text-center">
              Vérification du lien d&apos;invitation…
            </p>
          ) : verifyState === 'expired' ? (
            <div
              role="alert"
              className="rounded-cime bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700"
            >
              {isClient
                ? 'Lien expiré ou déjà utilisé. Demandez une nouvelle invitation à votre avocat.'
                : 'Lien expiré ou déjà utilisé. Demandez une nouvelle invitation à un administrateur du cabinet.'}
            </div>
          ) : (
          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
            {/* New password */}
            <div className="flex flex-col gap-1">
              <label
                htmlFor="password"
                className="text-sm font-medium text-neutral-700"
              >
                Nouveau mot de passe
              </label>
              <input
                id="password"
                type="password"
                autoComplete="new-password"
                placeholder="12 caractères minimum"
                className="w-full h-10 px-3 text-sm rounded-cime border border-neutral-300 bg-white placeholder:text-neutral-400 text-neutral-900 focus:outline-none focus:ring-2 focus:ring-cime-primary focus:border-transparent transition-colors"
                aria-invalid={!!errors.password}
                {...register('password', {
                  required: 'Le mot de passe est requis',
                  minLength: {
                    value: 12,
                    message: 'Le mot de passe doit contenir au moins 12 caractères',
                  },
                })}
              />
              {errors.password ? (
                <p className="text-xs text-red-600" role="alert">
                  {errors.password.message}
                </p>
              ) : null}
            </div>

            {/* Confirm password */}
            <div className="flex flex-col gap-1">
              <label
                htmlFor="confirmPassword"
                className="text-sm font-medium text-neutral-700"
              >
                Confirmer le mot de passe
              </label>
              <input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                placeholder="Répétez le mot de passe"
                className="w-full h-10 px-3 text-sm rounded-cime border border-neutral-300 bg-white placeholder:text-neutral-400 text-neutral-900 focus:outline-none focus:ring-2 focus:ring-cime-primary focus:border-transparent transition-colors"
                aria-invalid={!!errors.confirmPassword}
                {...register('confirmPassword', {
                  required: 'Veuillez confirmer votre mot de passe',
                  validate: (value) =>
                    value === passwordValue ||
                    'Les mots de passe ne correspondent pas',
                })}
              />
              {errors.confirmPassword ? (
                <p className="text-xs text-red-600" role="alert">
                  {errors.confirmPassword.message}
                </p>
              ) : null}
            </div>

            {submitError ? (
              <div
                role="alert"
                className="rounded-cime bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700"
              >
                {submitError}
              </div>
            ) : null}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              isLoading={isSubmitting}
              className="w-full mt-2"
            >
              Accéder à mon espace
            </Button>
          </form>
          )}
        </div>
      </div>
    </div>
  );
}
